/* eslint-disable @typescript-eslint/indent */
import { Button, Center, FormControl, FormErrorMessage, FormLabel, Input, Spinner, Text, Textarea, useToast, VStack } from "@chakra-ui/react"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import { UpdatePhoneDeviceMutationVariables, usePhoneDeviceByIdQuery, useUpdatePhoneDeviceMutation } from "../graphql"

type UpdatePhoneDeviceFormValues = UpdatePhoneDeviceMutationVariables["input"]

const validationSchema = yup.object<Record<keyof UpdatePhoneDeviceFormValues, yup.AnySchema<any, any, any> | Reference<unknown> | Lazy<any, any>>>({
	label: yup.object({
		name: yup.string().required().label("Name"),
		description: yup.string().nullable().label("Description"),
	}),
	deviceId: yup.string().required().label("Device ID"),
})

export type UpdatePhoneDeviceFormProps = {
	phoneDeviceId: string
}

export const UpdatePhoneDeviceForm: React.FC<UpdatePhoneDeviceFormProps> = ({ phoneDeviceId }) => {
	const [{ data, fetching, error }] = usePhoneDeviceByIdQuery({ variables: { phoneDeviceId }, pause: !phoneDeviceId })

	const [{ fetching: updating }, updatePhoneDevice] = useUpdatePhoneDeviceMutation()
	const toast = useToast()
	const navigate = useNavigate()

	if (fetching && !data?.phoneDeviceById) {
		return (
			<Center w="full" py="4">
				<VStack w="full" color="grayscale.label">
					<Text fontSize="sm">Loading phone device</Text>
					<Spinner size="sm" />
				</VStack>
			</Center>
		)
	}

	if (error) {
		return (
			<Center py="4">
				<Text fontSize="sm" fontWeight="semibold" color="error.500">
					{error.message.replace("[GraphQL] ", "")}
				</Text>
			</Center>
		)
	}

	if (!data?.phoneDeviceById) {
		return (
			<Center py="4">
				<Text fontSize="sm" fontWeight="semibold" color="error.500">
					Couldn&apos;t find the phone device.
				</Text>
			</Center>
		)
	}

	const formik = useFormik<UpdatePhoneDeviceFormValues>({
		initialValues: {
			label: { name: data.phoneDeviceById.label.name, description: data.phoneDeviceById.label.description },
			deviceId: data.phoneDeviceById.deviceId,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: async (values) => {
			const { data: updateData, error: updateError } = await updatePhoneDevice({ phoneDeviceId, input: values })

			if (updateError) {
				return toast({
					description: updateError.message.replace("[GraphQL] ", ""),
					status: "error",
				})
			}

			if (updateData?.updatePhoneDevice) {
				toast({ description: "Phone device updated successfully", status: "success" })
				navigate(`/phoneDevices/${updateData.updatePhoneDevice._id}`, { replace: true })

				return
			}
		},
	})

	return (
		<VStack as="form" onSubmit={(e) => formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)} w="full" align="stretch" spacing={6}>
			<VStack w="full" align="stretch">
				<FormControl isInvalid={Boolean(formik.touched.label?.name && formik.errors.label?.name)} isRequired>
					<FormLabel fontWeight="bold">Name</FormLabel>

					<Input variant="filled" bgColor="grayscale.input-background" placeholder="Enter name" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("label.name")} />

					<FormErrorMessage>{formik.errors.label?.name}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.label?.description && formik.errors.label?.description)}>
					<FormLabel fontWeight="bold">Description</FormLabel>

					<Textarea variant="filled" bgColor="grayscale.input-background" placeholder="Enter description" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("label.description")} />

					<FormErrorMessage>{formik.errors.label?.description}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.deviceId && formik.errors.deviceId)} isRequired>
					<FormLabel fontWeight="bold">Device ID</FormLabel>

					<Input variant="filled" bgColor="grayscale.input-background" placeholder="Enter device ID" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("deviceId")} />

					<FormErrorMessage>{formik.errors.deviceId}</FormErrorMessage>
				</FormControl>
			</VStack>
			<Button type="submit" colorScheme="primary" isLoading={updating}>
				Update
			</Button>
		</VStack>
	)
}
